import React, { useEffect, useState } from "react";
import {
  search_value,
  Filter,
  BellPlus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  BellRing,
  Search,
  RotateCwIcon,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useNotifications } from "../../hooks/useNotifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment/moment";
import NotificationForm from "./NotificationForm";
import { toast } from "react-toastify";
import { useResolvedPath } from "react-router-dom";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import ActionButton from "./ActionButton";

function NotificationManagement() {
  const [notificationsList, setNotificationsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [statusFilter, setStatusFilter] = useState({
    // notification_type: "BROADCAST",
    filter: JSON.stringify({}),
  });
  const [permission, setPermission] = useState({});
  const [updateMode, setUpdateMode] = useState(false);
  const queryClient = useQueryClient();
  const path = useResolvedPath();
  const [deleteModal, setDeleteModal] = useState(false);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: Math.ceil(notificationsList.length / 10),
    showing: 10,
    total: 0,
  });

  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const {
    fetchNotification,
    createNotification,
    deleteNotification,
    updateNotification,
    resendNotification,
  } = useNotifications({
    filters: { ...statusFilter, search_value: searchQuery },
    pagination: { page: pageData.currentPage, limit: 10 },
    sort: { field: sortField, direction: sortDirection },
  });

  const notificationsQuery = useQuery({
    queryKey: [
      "notifications",
      pageData.currentPage,
      statusFilter,
      searchQuery,
      sortField,
      sortDirection,
    ],
    queryFn: fetchNotification,
    keepPreviousData: true,
  });

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : "asc"
      );
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPageData((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const openModal = (notification, mode) => {
    setSelectedNotification(notification);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNotification(null);
  };

  const handlePageChange = (page) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  useEffect(() => {
    if (notificationsQuery.data && !notificationsQuery.isLoading) {
      const response = notificationsQuery.data;
      setNotificationsList(response?.data?.notification || []);
      setPageData(() => ({
        currentPage: response?.data?.pagination?.currentPage || 1,
        totalPages: response?.data?.pagination?.lastPage || 1,
        total: response?.data?.pagination?.total || 0,
        showing: response?.data?.pagination?.showing || 10,
      }));
    }
  }, [
    notificationsQuery.data,
    pageData.showing,
    pageData.currentPage,
    notificationsQuery.isLoading,
  ]);

  const handleUpdate = (notification) => {
    openModal(notification, "edit");
    setUpdateMode(true);
  };

  const handleDelete = {
    onDelete: (notification) =>
      mutation.deleteNotification.mutate(notification._id, {
        onSuccess: async (data) => {
          toast.success(data.message || "Notification Deleted");
          setDeleteModal(false);
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (notification) => {
      setDeleteModal(true);
      setSelectedNotification(notification);
    },
  };

  const mutation = {
    createNotification: useMutation({
      mutationFn: updateMode
        ? async (body) =>
            await updateNotification({ id: body._id, userData: body })
        : async (body) =>
            await createNotification(
              body,
              body?.notification_type || "BROADCAST"
            ),
    }),
    deleteNotification: useMutation({
      mutationFn: async (id) => await deleteNotification(id),
    }),
    resendNotification: useMutation({
      mutationFn: async (item) => await resendNotification(item._id),
    }),
  };
  const handleSubmit = (body) => {
    mutation.createNotification.mutate(body, {
      onSuccess: async (data) => {
        toast.success(
          data?.message ||
            `Notification ${updateMode ? "Updated" : "Created"} successfully`
        );
        queryClient.invalidateQueries(["notifications"]);
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  const handleResend = (body) => {
    mutation.resendNotification.mutate(body, {
      onSuccess: async (data) => {
        toast.success(data?.message || `Notification Resent successfully`);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  return (
    <div>
      <main>
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Notification Management</h1>
          {permission.add && (
            <button
              onClick={() => openModal({}, "create")}
              className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
            >
              <BellPlus className="mr-2 h-4 w-4" />
              Add New Notification
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-10/12 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter.filter}
                onChange={(e) =>
                  setStatusFilter({
                    ...statusFilter,
                    filter: e.target.value,
                  })
                }
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value={JSON.stringify({})}>All</option>
                <option
                  value={JSON.stringify({
                    field: "notification_type",
                    value: "BROADCAST",
                  })}
                >
                  Broadcast Notifications
                </option>
                <option
                  value={JSON.stringify({
                    field: "notification_type",
                    value: "MESSAGE",
                  })}
                >
                  Message Notifications
                </option>
                <option
                  value={JSON.stringify({
                    field: "notification_type",
                    value: "OTHER",
                  })}
                >
                  Other Notifications
                </option>
                {/* <option
                  value={JSON.stringify({
                    field: "notification_type",
                    value: "FRIEND_REQUEST",
                  })}
                >
                  Friend Request Notifications
                </option> */}
              </select>
              <button
                className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter({
                    filter: JSON.stringify({}),
                  });
                  setPageData((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                }}
              >
                <RotateCwIcon className="h-4 w-4" />
                <span className="ml-2">Reset</span>
              </button>
            </div>
          </div>

          {notificationsQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("full_name")}
                    >
                      Notification Type
                      {sortField === "notification_type" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("email")}
                    >
                      Title
                      {sortField === "header" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("created_at")}
                    >
                      Sent Date
                      {sortField === "created_at" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {notificationsList.length > 0 ? (
                    notificationsList.map((notification) => (
                      <tr
                        key={notification._id}
                        className="hover:bg-gray-50 border-b"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <BellRing className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900 capitalize md:uppercase">
                                {notification.notification_type}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {notification.header || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(notification.created_at).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <ActionButton
                            handleDelete={handleDelete}
                            item={notification}
                            openModal={openModal}
                            setPermission={setPermission}
                            handleResend={(data) => handleResend(data)}
                            isReSending={mutation.resendNotification.isPending}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No notifications found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handlePageChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>
      </main>

      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedNotification)}
        title="Notification"
      />

      {/* Notification Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Notification Details"
                  : modalMode === "edit"
                  ? "Edit Notification"
                  : "Create Notification"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <NotificationForm
                mode={modalMode}
                defaultValues={
                  {
                    ...selectedNotification,
                    created_at: moment(selectedNotification.created_at).format(
                      "YYYY-MM-DD"
                    ),
                  } || {}
                }
                onCancel={closeModal}
                onSubmit={(data) => {
                  handleSubmit(data);
                }}
                path={path.pathname}
                submissionLoading={mutation.createNotification.isPending}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationManagement;
